import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { ExerciseInput, ExerciseObjectiveInput } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useArchiveExercise,
  useCreateExercise,
  useDuplicateExercise,
  useExercise,
  useExerciseConfig,
  useExercises,
  useUpdateExercise,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { areaPerPlayer, totalWorkMinutes, workRestRatio } from '@/lib/exerciseMaths';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';

/**
 * The tactical board is ~4,200 lines of canvas renderer. It loads only when a
 * coach opens it, so the library list stays fast on a phone.
 */
const FieldCanvas = lazy(() => import('@/diagrams/FieldCanvas'));

const DIMENSIONS = ['tactical', 'technical', 'physical', 'mental', 'social'] as const;

const DIMENSION_STYLE: Record<string, string> = {
  tactical: 'bg-load-moderate/14 text-load-moderate',
  technical: 'bg-load-low/12 text-load-low',
  physical: 'bg-load-high/12 text-load-high',
  mental: 'bg-load-rest/12 text-load-rest',
  social: 'bg-surface-sunken text-ink-muted',
};

function blank(): ExerciseInput {
  return {
    name: '',
    objectives: [],
    energySystems: [],
    qualities: [],
    tags: [],
  } as unknown as ExerciseInput;
}

export function Exercises({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const config = useExerciseConfig();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const list = useExercises(search || undefined, category || undefined);

  const [editingId, setEditingId] = useState<string | null>(null);
  const detail = useExercise(editingId);
  const create = useCreateExercise();
  const update = useUpdateExercise();
  const duplicate = useDuplicateExercise();
  const archive = useArchiveExercise();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [open, setOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [form, setForm] = useState<ExerciseInput>(blank);

  const categories = config.data?.categories ?? [];
  const systems = config.data?.systems ?? [];
  const qualities = config.data?.qualities ?? [];

  /** Seeded config carries an i18n key; a coach's own rows keep their label. */
  const label = (row: { label: string; i18nKey: string | null }) => {
    if (!row.i18nKey) return row.label;
    const translated = t(row.i18nKey);
    return translated === row.i18nKey ? row.label : translated;
  };

  const set = <K extends keyof ExerciseInput>(k: K) => (value: ExerciseInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const num = (v: string): number | undefined => (v === '' ? undefined : Number(v));

  const startCreate = () => {
    setEditingId(null);
    setForm(blank());
    setOpen(true);
    setBoardOpen(false);
  };

  const startEdit = async (id: string) => {
    setEditingId(id);
    setOpen(true);
    setBoardOpen(false);
  };

  // Loading an exercise for editing fills the form once its detail arrives.
  const loadedId = detail.data?.id;
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  if (loadedId && editingId === loadedId && hydratedFor !== loadedId && detail.data) {
    const d = detail.data;
    setForm({
      name: d.name,
      categoryKey: d.categoryKey ?? undefined,
      description: d.description ?? undefined,
      minPlayers: d.minPlayers ?? undefined,
      maxPlayers: d.maxPlayers ?? undefined,
      areaLength: d.areaLength ?? undefined,
      areaWidth: d.areaWidth ?? undefined,
      durationMin: d.durationMin ?? undefined,
      sets: d.sets ?? undefined,
      reps: d.reps ?? undefined,
      workSeconds: d.workSeconds ?? undefined,
      restSeconds: d.restSeconds ?? undefined,
      intensity: d.intensity ?? undefined,
      organization: d.organization ?? undefined,
      rules: d.rules ?? undefined,
      coachingPoints: d.coachingPoints ?? undefined,
      progression: d.progression ?? undefined,
      regression: d.regression ?? undefined,
      successCriteria: d.successCriteria ?? undefined,
      observationCriteria: d.observationCriteria ?? undefined,
      equipment: d.equipment ?? undefined,
      rationale: d.rationale ?? undefined,
      diagramJson: d.diagramJson ?? undefined,
      objectives: d.objectives.map((o) => ({
        dimension: o.dimension as ExerciseObjectiveInput['dimension'],
        emphasis: o.emphasis as ExerciseObjectiveInput['emphasis'],
        text: o.text,
      })),
      energySystems: d.energySystems.map((e) => ({
        systemKey: e.systemKey,
        emphasis: e.emphasis as 'primary' | 'secondary',
      })),
      qualities: d.qualities.map((q) => ({
        qualityKey: q.qualityKey,
        emphasis: q.emphasis as 'primary' | 'secondary',
      })),
      tags: d.tags,
    });
    setHydratedFor(loadedId);
  }

  const perPlayer = useMemo(
    () => areaPerPlayer(form.areaLength, form.areaWidth, form.maxPlayers ?? form.minPlayers),
    [form.areaLength, form.areaWidth, form.maxPlayers, form.minPlayers],
  );
  const ratio = workRestRatio(form.workSeconds, form.restSeconds);
  const totalWork = totalWorkMinutes(form.workSeconds, form.reps, form.sets);

  const exercises = list.data ?? [];

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
    setHydratedFor(null);
    setBoardOpen(false);
  };

  return (
    <>
      <PageHeader
        title={t('exercises.title')}
        count={t('exercises.count', { count: exercises.length })}
        lead={t('exercises.lead')}
        help={localePath(locale, '/guide/session')}
        action={
          <Button variant="primary" onClick={startCreate}>
            {t('exercises.add')}
          </Button>
        }
      />

      {open ? (
        <div className="mb-5">
          <Panel title={editingId ? t('exercises.edit') : t('exercises.add')}>
            <form
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  if (editingId) await update.mutateAsync({ exerciseId: editingId, input: form });
                  else await create.mutateAsync(form);
                });
                if (ok) closeForm();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Section title={t('exercises.sectionIdentity')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                  <Field label={t('exercises.name')} error={fieldError('name')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.name}
                        onChange={(e) => set('name')(e.target.value)}
                        required
                        autoFocus
                      />
                    )}
                  </Field>
                  <Field label={t('exercises.category')} optional>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={form.categoryKey ?? ''}
                        onChange={(e) => set('categoryKey')(e.target.value || undefined)}
                      >
                        <option value="">{t('exercises.none')}</option>
                        {categories.map((c) => (
                          <option key={c.key} value={c.key}>
                            {label(c)}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t('exercises.description')} optional>
                      {(p) => (
                        <TextArea
                          {...p}
                          value={form.description ?? ''}
                          onChange={(e) => set('description')(e.target.value || undefined)}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </Section>

              <Section title={t('exercises.sectionDesign')} explain="work_rest">
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                  <Field label={`${t('exercises.players')} — ${t('exercises.minPlayers')}`} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={60} value={form.minPlayers ?? ''}
                        onChange={(e) => set('minPlayers')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={`${t('exercises.players')} — ${t('exercises.maxPlayers')}`} optional error={fieldError('maxPlayers')}>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={60} value={form.maxPlayers ?? ''}
                        onChange={(e) => set('maxPlayers')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={`${t('exercises.area')} — ${t('exercises.areaLength')}`} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={130} value={form.areaLength ?? ''}
                        onChange={(e) => set('areaLength')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={`${t('exercises.area')} — ${t('exercises.areaWidth')}`} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={100} value={form.areaWidth ?? ''}
                        onChange={(e) => set('areaWidth')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.durationMin')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={180} value={form.durationMin ?? ''}
                        onChange={(e) => set('durationMin')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.sets')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={30} value={form.sets ?? ''}
                        onChange={(e) => set('sets')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.reps')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={100} value={form.reps ?? ''}
                        onChange={(e) => set('reps')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.intensity')} hint={t('exercises.intensityHint')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={0} max={10} step={0.5} value={form.intensity ?? ''}
                        onChange={(e) => set('intensity')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.workSeconds')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={1} max={3600} value={form.workSeconds ?? ''}
                        onChange={(e) => set('workSeconds')(num(e.target.value))} />
                    )}
                  </Field>
                  <Field label={t('exercises.restSeconds')} optional>
                    {(p) => (
                      <TextInput {...p} type="number" min={0} max={3600} value={form.restSeconds ?? ''}
                        onChange={(e) => set('restSeconds')(num(e.target.value))} />
                    )}
                  </Field>
                </div>

                {/* Arithmetic on what was entered, shown as such. */}
                {perPlayer || ratio || totalWork ? (
                  <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-meta text-ink-muted">
                    {perPlayer ? <span>{t('exercises.areaPerPlayer', { value: perPlayer })}</span> : null}
                    {ratio ? <span>{t('exercises.workRest', { ratio })}</span> : null}
                    {totalWork ? <span className="tabular">{totalWork} min</span> : null}
                  </p>
                ) : null}
              </Section>

              <Section title={t('exercises.sectionPurpose')} hint={t('exercises.objectivesHint')}>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    {form.objectives.map((o, i) => (
                      <div key={i} className="grid gap-2 sm:grid-cols-[9rem_9rem_1fr_auto]">
                        <SelectInput
                          value={o.dimension}
                          onChange={(e) =>
                            set('objectives')(
                              form.objectives.map((x, j) =>
                                j === i
                                  ? { ...x, dimension: e.target.value as typeof x.dimension }
                                  : x,
                              ),
                            )
                          }
                        >
                          {DIMENSIONS.map((d) => (
                            <option key={d} value={d}>
                              {t(`exercises.dim_${d}`)}
                            </option>
                          ))}
                        </SelectInput>
                        <SelectInput
                          value={o.emphasis}
                          onChange={(e) =>
                            set('objectives')(
                              form.objectives.map((x, j) =>
                                j === i
                                  ? { ...x, emphasis: e.target.value as typeof x.emphasis }
                                  : x,
                              ),
                            )
                          }
                        >
                          <option value="primary">{t('exercises.emphasis_primary')}</option>
                          <option value="secondary">{t('exercises.emphasis_secondary')}</option>
                        </SelectInput>
                        <TextInput
                          value={o.text}
                          placeholder={t('exercises.objectiveText')}
                          onChange={(e) =>
                            set('objectives')(
                              form.objectives.map((x, j) =>
                                j === i ? { ...x, text: e.target.value } : x,
                              ),
                            )
                          }
                        />
                        <Button
                          type="button"
                          variant="quiet"
                          className="!min-h-11 !px-3"
                          onClick={() =>
                            set('objectives')(form.objectives.filter((_, j) => j !== i))
                          }
                          aria-label={t('common.close')}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        className="!text-meta"
                        onClick={() =>
                          set('objectives')([
                            ...form.objectives,
                            {
                              dimension: 'tactical',
                              emphasis: form.objectives.some((o) => o.emphasis === 'primary')
                                ? 'secondary'
                                : 'primary',
                              text: '',
                            },
                          ])
                        }
                      >
                        {t('exercises.addObjective')}
                      </Button>
                      {fieldError('objectives') ? (
                        <span className="ms-3 text-meta text-risk">{fieldError('objectives')}</span>
                      ) : null}
                    </div>
                  </div>

                  <fieldset>
                    <legend className="text-meta font-medium">{t('exercises.energySystems')}</legend>
                    <p className="mt-0.5 max-w-[72ch] text-meta text-ink-muted">
                      {t('exercises.energyHint')}
                    </p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      {systems.map((sys) => {
                        const current = form.energySystems.find((e) => e.systemKey === sys.key);
                        return (
                          <SelectInput
                            key={sys.key}
                            value={current?.emphasis ?? ''}
                            onChange={(e) => {
                              const others = form.energySystems.filter(
                                (x) => x.systemKey !== sys.key,
                              );
                              set('energySystems')(
                                e.target.value
                                  ? [
                                      ...others,
                                      {
                                        systemKey: sys.key,
                                        emphasis: e.target.value as 'primary' | 'secondary',
                                      },
                                    ]
                                  : others,
                              );
                            }}
                            aria-label={label(sys)}
                          >
                            <option value="">{label(sys)} — {t('exercises.none')}</option>
                            <option value="primary">
                              {label(sys)} — {t('exercises.emphasis_primary')}
                            </option>
                            <option value="secondary">
                              {label(sys)} — {t('exercises.emphasis_secondary')}
                            </option>
                          </SelectInput>
                        );
                      })}
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className="text-meta font-medium">{t('exercises.qualities')}</legend>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {qualities.map((q) => {
                        const active = form.qualities.some((x) => x.qualityKey === q.key);
                        return (
                          <button
                            key={q.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() =>
                              set('qualities')(
                                active
                                  ? form.qualities.filter((x) => x.qualityKey !== q.key)
                                  : [...form.qualities, { qualityKey: q.key, emphasis: 'secondary' }],
                              )
                            }
                            className={`min-h-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                              active
                                ? 'border-ink bg-ink text-paper'
                                : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                            }`}
                          >
                            {label(q)}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <Field
                    label={t('exercises.rationale')}
                    hint={t('exercises.rationaleHint')}
                    optional
                  >
                    {(p) => (
                      <TextArea
                        {...p}
                        value={form.rationale ?? ''}
                        onChange={(e) => set('rationale')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section title={t('exercises.sectionCoaching')}>
                <div className="grid gap-x-5 gap-y-2 lg:grid-cols-2">
                  {([
                    ['organization', 'organization'],
                    ['rules', 'rules'],
                    ['coachingPoints', 'coachingPoints'],
                    ['progression', 'progression'],
                    ['regression', 'regression'],
                    ['successCriteria', 'successCriteria'],
                    ['observationCriteria', 'observationCriteria'],
                    ['equipment', 'equipment'],
                  ] as const).map(([field, key]) => (
                    <Field key={field} label={t(`exercises.${key}`)} optional>
                      {(p) => (
                        <TextArea
                          {...p}
                          value={(form[field] as string | undefined) ?? ''}
                          onChange={(e) => set(field)((e.target.value || undefined) as never)}
                        />
                      )}
                    </Field>
                  ))}
                  <div className="lg:col-span-2">
                    <Field label={t('exercises.tags')} hint={t('exercises.tagsHint')} optional>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={form.tags.join(', ')}
                          onChange={(e) =>
                            set('tags')(
                              e.target.value
                                .split(',')
                                .map((x) => x.trim())
                                .filter(Boolean),
                            )
                          }
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </Section>

              <Section title={t('exercises.sectionDiagram')}>
                {boardOpen ? (
                  <div className="grid gap-3">
                    <Suspense
                      fallback={
                        <p className="flex items-center gap-2 py-8 text-ink-muted">
                          <Spinner /> {t('common.loading')}
                        </p>
                      }
                    >
                      <FieldCanvas
                        initialData={form.diagramJson}
                        onChange={(data) => set('diagramJson')(data)}
                      />
                    </Suspense>
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-fit"
                      onClick={() => setBoardOpen(false)}
                    >
                      {t('exercises.closeBoard')}
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="secondary" onClick={() => setBoardOpen(true)}>
                    {form.diagramJson ? t('exercises.sectionDiagram') : t('exercises.openBoard')}
                  </Button>
                )}
              </Section>

              <div className="flex flex-wrap gap-2 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('exercises.save')}
                </Button>
                <Button type="button" variant="quiet" onClick={closeForm}>
                  {t('common.cancel')}
                </Button>
                {editingId ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={async () => {
                        await duplicate.mutateAsync({
                          exerciseId: editingId,
                          name: t('exercises.duplicateName', { name: form.name }),
                        });
                        closeForm();
                      }}
                    >
                      {t('exercises.duplicate')}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      className="ms-auto"
                      onClick={async () => {
                        if (!confirm(t('exercises.archiveConfirm'))) return;
                        await archive.mutateAsync(editingId);
                        closeForm();
                      }}
                    >
                      {t('exercises.archive')}
                    </Button>
                  </>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Field label={t('exercises.search')}>
            {(p) => (
              <TextInput
                {...p}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('exercises.searchPlaceholder')}
              />
            )}
          </Field>
        </div>
        <div className="w-52">
          <Field label={t('exercises.category')}>
            {(p) => (
              <SelectInput {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">{t('exercises.allCategories')}</option>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {label(c)}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>
        </div>
      </div>

      <Panel>
        {list.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : exercises.length === 0 ? (
          <EmptyState
            title={t('exercises.empty')}
            body={t('exercises.emptyBody')}
            action={
              <Button variant="primary" onClick={startCreate}>
                {t('exercises.add')}
              </Button>
            }
          />
        ) : (
          <Sheet>
            {exercises.map((ex) => {
              const cat = categories.find((c) => c.key === ex.categoryKey);
              return (
                <SheetRow key={ex.id} onClick={() => void startEdit(ex.id)}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{ex.name}</span>
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-meta text-ink-faint">
                      {ex.minPlayers || ex.maxPlayers ? (
                        <span className="tabular">
                          {ex.minPlayers ?? '?'}–{ex.maxPlayers ?? '?'} {t('exercises.players')}
                        </span>
                      ) : null}
                      {ex.areaLength && ex.areaWidth ? (
                        <span className="tabular">
                          {ex.areaLength}×{ex.areaWidth} m
                        </span>
                      ) : null}
                      {ex.durationMin ? <span className="tabular">{ex.durationMin} min</span> : null}
                      {ex.diagramJson ? <span>▦</span> : null}
                    </span>
                  </span>
                  {cat ? (
                    <span
                      className={`rounded-[3px] px-1.5 py-0.5 text-meta ${
                        DIMENSION_STYLE.tactical
                      }`}
                    >
                      {label(cat)}
                    </span>
                  ) : null}
                  {ex.intensity !== null ? (
                    <Chip muted>
                      <span className="tabular">RPE {ex.intensity}</span>
                    </Chip>
                  ) : null}
                </SheetRow>
              );
            })}
          </Sheet>
        )}
      </Panel>
    </>
  );
}
